import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux'
import { success } from 'features/toast/Toast'
import { BiddingStrategyTypes } from 'features/Codes'
import {
  reset as resetKeywordsBiddingStrategy,
  setErrors,
  setSubmitting,
  setWarnings,
} from 'features/dashboard/operation/keywordBiddingStrategyEdit/keywordsBiddingStrategyEditSlice'
import api from 'api/axios/foka'
import { useCallback, useEffect, useState } from 'react'
import useValidator from 'utils/hooks/useValidator'
import { useErrorToast } from 'utils/hooks/useErrorToast'
import { integerValue, isEmptyObject } from 'utils'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import {
  exitEditable,
  KeywordTableEditTypes,
  setSelectedIds,
} from 'features/dashboard/table/keyword/keywordTableSlice'

const useKeywordBiddingStrategyEdit = () => {
  const dispatch = useDispatch()
  const [apiError, setApiError] = useState()

  useErrorToast({ error: apiError, handleClearError: () => setApiError(null) })

  const {
    table: {
      keywordTable: { content, editable, editType },
    },
    operation: {
      keywordBiddingStrategy: { strategies },
    },
  } = useSelector(state => state.dashboard, shallowEqual)
  const isActive =
    editable && editType === KeywordTableEditTypes.maxBiddingAmount
  const { errors: validationErrors, validate: validateErrors } = useValidator(
    () =>
      strategies.reduce((errors, strategy) => {
        const error =
          strategy.biddingStrategyType ===
            BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING &&
          ((strategy.maxBiddingAmount > 100000 &&
            '입찰가는 10만원 이하 10원 단위로 등록 가능합니다.') ||
            (strategy.maxBiddingAmount < 70 &&
              '입찰가는 70원 이상 10원 단위로 등록 가능합니다.') ||
            (strategy.maxBiddingAmount % 10 !== 0 &&
              '입찰가는 10원 단위로 등록 가능합니다.'))
        return {
          ...errors,
          ...(error && {
            [strategy.keywordId]: error,
          }),
        }
      }, {}),
    [strategies]
  )

  const { errors: validationWarnings, validate: validateWarnings } =
    useValidator(
      () =>
        strategies.reduce((errors, strategy) => {
          const {
            biddingStrategy,
            parentDomainInfo: { adGroup },
          } = content.find(item => item.id === strategy.keywordId)
          const before =
            biddingStrategy.type === BiddingStrategyTypes.INHERIT_FROM_ADGROUP
              ? adGroup.maxBiddingAmount
              : biddingStrategy.maxBiddingAmount
          const after =
            strategy.biddingStrategyType ===
            BiddingStrategyTypes.INHERIT_FROM_ADGROUP
              ? adGroup.maxBiddingAmount
              : integerValue(strategy.maxBiddingAmount)
          const percentage = (after / before) * 100
          const fixed = Math.ceil(percentage.toFixed(1))
          const error =
            isEmptyObject(validationErrors) &&
            after &&
            fixed >= 500 &&
            `${fixed.toLocaleString()}%로 증가폭이 큽니다.`
          return {
            ...errors,
            ...(error && {
              [strategy.keywordId]: error,
            }),
          }
        }, {}),
      [strategies, validationErrors]
    )

  useEffect(() => {
    dispatch(setErrors(validationErrors))
  }, [dispatch, validationErrors])

  useEffect(() => {
    dispatch(setWarnings(validationWarnings))
  }, [dispatch, validationWarnings])

  const reset = useCallback(
    () => dispatch(resetKeywordsBiddingStrategy()),
    [dispatch]
  )

  const apply = useCallback(async () => {
    dispatch(setSubmitting(true))
    try {
      await api.dashboard.patchKeywordsBiddingStrategy(strategies)
      success('키워드 변경사항이 적용되었습니다.')
      batch(() => {
        dispatch(exitEditable())
        dispatch(resetKeywordsBiddingStrategy())
        dispatch(setSelectedIds([]))
        dispatch(refreshDashboard())
      })
    } catch (e) {
      setApiError(e)
    }
    dispatch(setSubmitting(false))
  }, [dispatch, strategies])

  return { isActive, reset, apply, validateErrors, validateWarnings }
}
export default useKeywordBiddingStrategyEdit
